import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { environment } from '@workspace/ng-config';
import { Observable } from 'rxjs';

@Service()
export class ApiCaller {
  private http = inject(HttpClient);

  buildUrl(apiPrefix: string, endpoint: string): string {
    const baseUrl = environment.localhost ? '' : environment.apps.publicUrl;
    const cleanEndpoint = endpoint ? `/${endpoint}` : '';
    return `${baseUrl}/api/${apiPrefix}${cleanEndpoint}`;
  }

  getRequest<R>(apiPrefix: string, endpoint = '', options?: unknown): Observable<R> {
    const url = this.buildUrl(apiPrefix, endpoint);
    return this.http.get<R>(url, options as Parameters<HttpClient['get']>[1]);
  }

  postRequest<R, P = unknown>(apiPrefix: string, endpoint = '', payload?: P): Observable<R> {
    return this.http.post<R>(this.buildUrl(apiPrefix, endpoint), payload);
  }

  putRequest<R, P = unknown>(apiPrefix: string, endpoint = '', payload?: P): Observable<R> {
    return this.http.put<R>(this.buildUrl(apiPrefix, endpoint), payload);
  }

  // TODO find a way to enforce payload type
  patchRequest<R, P = unknown>(apiPrefix: string, endpoint = '', payload?: P): Observable<R> {
    return this.http.patch<R>(this.buildUrl(apiPrefix, endpoint), payload);
  }

  deleteRequest<R>(apiPrefix: string, endpoint = ''): Observable<R> {
    return this.http.delete<R>(this.buildUrl(apiPrefix, endpoint));
  }

  multipartRequest<T, R>(
    apiPrefix: string,
    endpoint = '',
    verb: 'post' | 'put' | 'patch',
    data: T,
    file?: File,
    customHeaders?: HttpHeaders,
  ): Observable<R> {
    const url = this.buildUrl(apiPrefix, endpoint);
    const formData = new FormData();
    if (file) {
      formData.append('file', file, file.name);
    }
    if (data && typeof data === 'object') {
      Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });
    }
    formData.append('data', JSON.stringify(data));
    let headers = new HttpHeaders();

    if (customHeaders) {
      customHeaders.keys().forEach((key) => {
        const values = customHeaders.getAll(key);
        if (values !== null) {
          headers = headers.set(key, values);
        }
      });
    }
    switch (verb) {
      case 'put':
        return this.http.put<R>(url, formData, { headers });
      case 'post':
        return this.http.post<R>(url, formData, { headers });
      case 'patch':
        return this.http.patch<R>(url, formData, { headers });
      default:
        throw new Error('Unsupported HTTP verb');
    }
  }
}
